import React from 'react';
import PageShell from '../components/PageShell';
import { Search, MessagesSquare, X } from 'lucide-react';
import { cpFetch, createAuthHeaders } from '../lib/cpClient';
import { useAuthStore } from '../store/authStore';
import { useLocation } from 'react-router-dom';

type McpEventRow = {
  time: string;
  event_id: string;
  event_type: string;
  tenant_id: string;
  app_id: string;
  source: string;
  request_id?: string | null;
  tool_name?: string | null;
  agent_id?: string | null;
  workflow?: string | null;
  input_summary?: string | null;
  success?: boolean | null;
  error?: string | null;
  duration_ms?: number | null;
  builder_step?: string | null;
  builder_mode?: string | null;
  environment?: string | null;
};

type SearchResp = {
  ok: boolean;
  tenant_id: string;
  took_ms: number;
  limit: number;
  offset: number;
  has_more: boolean;
  items: McpEventRow[];
};

type ConversationResp = {
  ok: boolean;
  tenant_id: string;
  request_id: string;
  items: McpEventRow[];
};

function isoForDatetimeLocal(d: Date) {
  const pad = (n: number) => String(n).padStart(2, '0');
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

function coerceToDatetimeLocal(value: string): string | null {
  if (!value) return null;
  // Already datetime-local (no timezone, no seconds)
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return value;
  const d = new Date(value);
  if (!Number.isFinite(d.getTime())) return null;
  return isoForDatetimeLocal(d);
}

export default function McpEventsSearchPage() {
  const { token, selectedTenant } = useAuthStore();
  const location = useLocation();

  const authHeaders = React.useMemo(() => {
    if (!token || !selectedTenant) return null;
    return createAuthHeaders(token, selectedTenant);
  }, [token, selectedTenant]);

  const [q, setQ] = React.useState('');
  const [appId, setAppId] = React.useState('');
  const [agentId, setAgentId] = React.useState('');
  const [workflow, setWorkflow] = React.useState('');
  const [requestId, setRequestId] = React.useState('');
  const [since, setSince] = React.useState(() => isoForDatetimeLocal(new Date(Date.now() - 24 * 60 * 60 * 1000)));
  const [until, setUntil] = React.useState(() => isoForDatetimeLocal(new Date()));
  const [limit, setLimit] = React.useState(50);
  const [excludeSandbox, setExcludeSandbox] = React.useState(false);

  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);
  const [data, setData] = React.useState<SearchResp | null>(null);

  const [conv, setConv] = React.useState<ConversationResp | null>(null);
  const [convBusy, setConvBusy] = React.useState(false);
  const [convErr, setConvErr] = React.useState<string | null>(null);

  // Allow deep-linking (from health/app pages) with prefilled filters.
  const hydratedRef = React.useRef(false);
  const autoSearchRef = React.useRef(false);
  const autoOpenRequestIdRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;

    const params = new URLSearchParams(location.search);
    const nextQ = params.get('q');
    const nextAppId = params.get('app_id');
    const nextAgentId = params.get('agent_id');
    const nextWorkflow = params.get('workflow');
    const nextRequestId = params.get('request_id');
    const nextLimit = params.get('limit');
    const nextSince = params.get('since');
    const nextUntil = params.get('until');

    if (nextQ != null) setQ(nextQ);
    if (nextAppId != null) setAppId(nextAppId);
    if (nextAgentId != null) setAgentId(nextAgentId);
    if (nextWorkflow != null) setWorkflow(nextWorkflow);
    if (nextRequestId != null) setRequestId(nextRequestId);

    if (nextLimit != null) {
      const n = Number(nextLimit);
      if (Number.isFinite(n) && n > 0 && n <= 1000) setLimit(n);
    }

    const dlSince = nextSince ? coerceToDatetimeLocal(nextSince) : null;
    const dlUntil = nextUntil ? coerceToDatetimeLocal(nextUntil) : null;
    if (dlSince) setSince(dlSince);
    if (dlUntil) setUntil(dlUntil);

    autoSearchRef.current = params.get('auto') === '1' || params.get('auto') === 'true';
    if (nextRequestId) autoOpenRequestIdRef.current = nextRequestId;
  }, [location.search]);

  React.useEffect(() => {
    if (!authHeaders) return;
    if (autoSearchRef.current) {
      autoSearchRef.current = false;
      runSearch(0);
    }
    if (autoOpenRequestIdRef.current) {
      const rid = autoOpenRequestIdRef.current;
      autoOpenRequestIdRef.current = null;
      openConversation(rid);
    }
  }, [authHeaders]);

  async function runSearch(offset = 0) {
    if (!authHeaders) return;
    setBusy(true);
    setErr(null);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      if (appId.trim()) params.set('app_id', appId.trim());
      if (agentId.trim()) params.set('agent_id', agentId.trim());
      if (workflow.trim()) params.set('workflow', workflow.trim());
      if (requestId.trim()) params.set('request_id', requestId.trim());
      if (since) params.set('since', new Date(since).toISOString());
      if (until) params.set('until', new Date(until).toISOString());
      params.set('limit', String(limit));
      params.set('offset', String(offset));
      if (excludeSandbox) params.set('exclude_environment', 'sandbox');

      const resp = await cpFetch(`/mcp/events/search?${params.toString()}`, { headers: authHeaders });
      setData(resp as SearchResp);
    } catch (e: any) {
      setErr(e?.message || String(e));
      setData(null);
    } finally {
      setBusy(false);
    }
  }

  async function openConversation(reqId: string) {
    if (!authHeaders) return;
    setConvBusy(true);
    setConvErr(null);
    try {
      const resp = await cpFetch(`/mcp/conversations/${encodeURIComponent(reqId)}?include_input=1&limit=500`, { headers: authHeaders });
      setConv(resp as ConversationResp);
    } catch (e: any) {
      setConvErr(e?.message || String(e));
      setConv(null);
    } finally {
      setConvBusy(false);
    }
  }

  return (
    <PageShell
      title="Agent Conversations"
      subtitle="Search MCP/agent events persisted from Pulsar into TimescaleDB, and drill into conversations by request_id."
      icon={MessagesSquare}
      iconColor="bg-indigo-600"
    >
      <div className="space-y-4">
        <div className="rounded-xl border bg-white p-4 space-y-3">
          <div className="grid grid-cols-1 lg:grid-cols-6 gap-3">
            <Field label="Query">
              <input className="w-full border rounded px-3 py-2 text-sm" value={q} onChange={(e) => setQ(e.target.value)} placeholder="free text (input_summary, error, tool_name…)" />
            </Field>
            <Field label="App ID">
              <input className="w-full border rounded px-3 py-2 text-sm" value={appId} onChange={(e) => setAppId(e.target.value)} placeholder="app id" />
            </Field>
            <Field label="Agent ID">
              <input className="w-full border rounded px-3 py-2 text-sm" value={agentId} onChange={(e) => setAgentId(e.target.value)} placeholder="agent id" />
            </Field>
            <Field label="Workflow">
              <input className="w-full border rounded px-3 py-2 text-sm" value={workflow} onChange={(e) => setWorkflow(e.target.value)} placeholder="workflow" />
            </Field>
            <Field label="Request ID">
              <input className="w-full border rounded px-3 py-2 text-sm" value={requestId} onChange={(e) => setRequestId(e.target.value)} placeholder="request id" />
            </Field>
            <Field label="Limit">
              <input className="w-full border rounded px-3 py-2 text-sm" value={String(limit)} onChange={(e) => setLimit(Number(e.target.value || 50))} />
            </Field>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
            <Field label="Since">
              <input className="w-full border rounded px-3 py-2 text-sm" type="datetime-local" value={since} onChange={(e) => setSince(e.target.value)} />
            </Field>
            <Field label="Until">
              <input className="w-full border rounded px-3 py-2 text-sm" type="datetime-local" value={until} onChange={(e) => setUntil(e.target.value)} />
            </Field>
            <div className="flex items-end gap-2">
              <button
                className="inline-flex items-center gap-2 border rounded px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
                onClick={() => runSearch(0)}
                disabled={!authHeaders || busy}
                title={!authHeaders ? 'Select tenant and authenticate first' : 'Run search'}
              >
                <Search className="w-4 h-4" />
                {busy ? 'Searching…' : 'Search'}
              </button>
              {data?.has_more ? (
                <button
                  className="border rounded px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
                  onClick={() => runSearch((data?.offset || 0) + (data?.limit || 0))}
                  disabled={busy}
                >
                  Next page
                </button>
              ) : null}
            </div>
            <div className="flex flex-col justify-end gap-2">
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={excludeSandbox}
                  onChange={(e) => setExcludeSandbox(e.target.checked)}
                  className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                />
                <span>Hide sandbox events</span>
              </label>
              <div className="text-xs text-gray-500">
                {data ? <span>took {data.took_ms}ms · {data.items.length} rows</span> : <span>—</span>}
              </div>
            </div>
          </div>

          {err ? <div className="text-sm text-red-600">Error: {err}</div> : null}
        </div>

        <div className="rounded-xl border bg-white overflow-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-gray-50 text-gray-600">
              <tr className="text-left">
                <th className="py-2 px-3">time</th>
                <th className="py-2 px-3">type</th>
                <th className="py-2 px-3">app</th>
                <th className="py-2 px-3">env</th>
                <th className="py-2 px-3">agent</th>
                <th className="py-2 px-3">workflow</th>
                <th className="py-2 px-3">tool</th>
                <th className="py-2 px-3">ok</th>
                <th className="py-2 px-3">request_id</th>
              </tr>
            </thead>
            <tbody>
              {(data?.items || []).map((row) => (
                <tr
                  key={`${row.time}:${row.event_id}`}
                  className="border-t hover:bg-gray-50 cursor-pointer"
                  onClick={() => row.request_id ? openConversation(String(row.request_id)) : undefined}
                  title={row.request_id ? 'Click to open conversation' : 'No request_id to group by'}
                >
                  <td className="py-2 px-3 font-mono whitespace-nowrap">{new Date(row.time).toLocaleString()}</td>
                  <td className="py-2 px-3 font-mono">{row.event_type}</td>
                  <td className="py-2 px-3 font-mono">{row.app_id}</td>
                  <td className="py-2 px-3">
                    {row.environment === 'sandbox' ? (
                      <span className="inline-block px-1.5 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-300">sbx</span>
                    ) : (
                      <span className="text-gray-400 text-xs">prod</span>
                    )}
                  </td>
                  <td className="py-2 px-3 font-mono">{row.agent_id || '—'}</td>
                  <td className="py-2 px-3 font-mono">{row.workflow || '—'}</td>
                  <td className="py-2 px-3 font-mono">{row.tool_name || '—'}</td>
                  <td className="py-2 px-3">{row.success === false ? 'false' : row.success === true ? 'true' : '—'}</td>
                  <td className="py-2 px-3 font-mono max-w-[340px] truncate">{row.request_id || '—'}</td>
                </tr>
              ))}
              {!busy && (!data?.items || data.items.length === 0) ? (
                <tr>
                  <td className="py-6 px-3 text-sm text-gray-500" colSpan={8}>
                    No results yet. Run a search.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        {convBusy ? <div className="text-sm text-gray-500">Loading conversation…</div> : null}
        {convErr ? <div className="text-sm text-red-600">Conversation error: {convErr}</div> : null}

        {conv ? (
          <div className="rounded-xl border bg-white p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="font-semibold text-gray-900">
                Conversation: <span className="font-mono text-sm">{conv.request_id}</span>
              </div>
              <button className="text-sm border rounded px-3 py-1" onClick={() => setConv(null)}>
                <X className="w-4 h-4 inline mr-1" />
                Close
              </button>
            </div>
            <div className="space-y-2">
              {conv.items.map((e) => (
                <div key={`${e.time}:${e.event_id}`} className="rounded-lg border p-3">
                  <div className="flex flex-wrap gap-3 text-xs text-gray-600">
                    <span className="font-mono">{new Date(e.time).toLocaleString()}</span>
                    <span className="font-mono">{e.event_type}</span>
                    <span className="font-mono">{e.agent_id || '—'}</span>
                    <span className="font-mono">{e.workflow || '—'}</span>
                    <span className="font-mono">{e.tool_name || '—'}</span>
                    {typeof e.duration_ms === 'number' ? <span>{e.duration_ms}ms</span> : null}
                    {e.success === false ? <span className="text-red-600">failed</span> : e.success === true ? <span className="text-green-700">ok</span> : null}
                  </div>
                  {e.input_summary ? (
                    <div className="text-xs text-gray-800 mt-2 whitespace-pre-wrap break-words">{e.input_summary}</div>
                  ) : null}
                  {e.error ? (
                    <div className="text-xs text-red-700 mt-2 whitespace-pre-wrap break-words">{e.error}</div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </PageShell>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="text-xs text-gray-600">{label}</div>
      {children}
    </div>
  );
}

